// @file Dashboard settings store
// It wraps the dashboard settings Vuex store into a Pinia store.
import { normalizedOppositePaymentSchedule, normalizedPaymentSchedule } from '@@/bits/billing_helper'
import device from '@@/bits/device'
import { getVuexStore } from '@@/bits/pinia'
import { usePersonalPlansStore } from '@@/pinia/personal_plans'
import type { Library, LibraryId, Plan, Tenant, User, UserOptions } from '@@/types'
import type { ScheduledChange } from '@@/vuexstore/dashboard_settings'
import { type DashboardSettingsState, type LibraryDowngradeStep } from '@@/vuexstore/dashboard_settings'
import type { Billing } from '@padlet/arvo'
import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

export const useDashboardSettingsStore = defineStore('dashboardSettings', () => {
  // State

  // Getters
  const isApp = computed((): boolean => device.app)
  const isCancellingSoon = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isCancellingSoon)
  const isElectronApp = computed((): boolean => device.electronApp)
  const isNeonTier = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isNeonTier)
  const user = computed((): User => getVuexStore<DashboardSettingsState>()?.getters.user ?? { id: null })
  const userId = computed((): number => getVuexStore<DashboardSettingsState>()?.getters.userId)
  const userOptions = computed((): UserOptions => getVuexStore<DashboardSettingsState>()?.getters.userOptions)
  const tenant = computed((): Tenant => getVuexStore<DashboardSettingsState>()?.getters.tenant ?? { id: null })

  // Actions
  function changeConnectivity(isOnline: boolean): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('changeConnectivity', isOnline, { root: true })
  }

  function goBack(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('goBack', { root: true })
  }

  function resizeVisualViewport(payload: { width: number; height: number; scale: number }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('browser/resizeVisualViewport', payload, { root: true })
  }

  function resizeWindow(payload: { width: number; height: number }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('browser/resizeWindow', payload, { root: true })
  }

  function startOffboarding(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('startOffboarding', { root: true })
  }

  function toggleDarkMode(isDarkMode: boolean): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('browser/toggleDarkMode', isDarkMode, { root: true })
  }

  function updateCurrentTenant(payload: Partial<Tenant>): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('updateCurrentTenant', payload, { root: true })
  }

  function updateUserOptions(payload: Partial<UserOptions>): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('updateUserOptions', payload, { root: true })
  }

  function verifyIdentity(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('verifyIdentity', { root: true })
  }

  // Personal Billing
  const { billing, isBillingInitialized } = usePersonalBilling()

  // Library billing
  const {
    // State

    // Getters
    estimatedTeamNeonDowngradeRefundAmount,
    hasLibraryScheduledChanges,
    isLibraryPaying,
    isLoadingLibraryBillingInformation,
    isLoadingTeamNeonDowngradeInformation,
    isProcessingLibraryDowngrade,
    libraryBilling,
    libraryBillingError,
    libraryDowngradeError,
    libraryDowngradeStep,
    xLibraryDowngradeModal,

    // Actions
    changeStepLibraryDowngradeModal,
    closeLibraryDowngradeModal,
    downgradeLibraryToTeamNeonImmediately,
    scheduleLibraryDowngradeToTeamNeon,
  } = useLibraryBilling()

  // Library
  const {
    // State

    // Getters
    canInviteMembers,
    canInviteSchoolAdmins,
    canInviteSchoolStudents,
    canInviteSchoolTeachers,
    canManageMembers,
    canUpdateLibraryInfo,
    currentLibrary,
    isClassroomLibrary,
    isCurrentUserAtLeastLibraryTeacher,
    isCurrentUserLibraryAdmin,
    isCurrentUserLibraryOwner,
    isSchoolLibrary,
    isTeamLibrary,
    libraries,
    libraryId,
    libraryName,
    librarySlug,
    ownedLibraries,
    trialDaysLeft,

    // Actions
    fetchViewableLibraries,
  } = useLibrary()

  const {
    currentScheduledChange,
    oppositeSchedulePlan,
    hasScheduledChanges,
    isOfferedPlanCurrencyEqualToUserPlanCurrency,
    isWebPlan,
    showPlanCrossgradeModalCheckingCurrency,
    showScheduledChangeModal,
  } = usePersonalPlans(billing)

  return {
    // State

    // Getters
    billing,
    canInviteMembers,
    canInviteSchoolAdmins,
    canInviteSchoolStudents,
    canInviteSchoolTeachers,
    canManageMembers,
    canUpdateLibraryInfo,
    currentLibrary,
    currentScheduledChange,
    estimatedTeamNeonDowngradeRefundAmount,
    hasLibraryScheduledChanges,
    hasScheduledChanges,
    isApp,
    isBillingInitialized,
    isCancellingSoon,
    isClassroomLibrary,
    isCurrentUserAtLeastLibraryTeacher,
    isCurrentUserLibraryAdmin,
    isCurrentUserLibraryOwner,
    isElectronApp,
    isLibraryPaying,
    isLoadingLibraryBillingInformation,
    isLoadingTeamNeonDowngradeInformation,
    isNeonTier,
    isOfferedPlanCurrencyEqualToUserPlanCurrency,
    isProcessingLibraryDowngrade,
    isSchoolLibrary,
    isTeamLibrary,
    isWebPlan,
    libraries,
    libraryBilling,
    libraryBillingError,
    libraryDowngradeError,
    libraryDowngradeStep,
    libraryId,
    libraryName,
    librarySlug,
    oppositeSchedulePlan,
    ownedLibraries,
    tenant,
    trialDaysLeft,
    user,
    userId,
    userOptions,
    xLibraryDowngradeModal,

    // Actions
    changeConnectivity,
    changeStepLibraryDowngradeModal,
    closeLibraryDowngradeModal,
    downgradeLibraryToTeamNeonImmediately,
    fetchViewableLibraries,
    goBack,
    resizeVisualViewport,
    resizeWindow,
    scheduleLibraryDowngradeToTeamNeon,
    startOffboarding,
    toggleDarkMode,
    updateCurrentTenant,
    updateUserOptions,
    verifyIdentity,
    showPlanCrossgradeModalCheckingCurrency,
    showScheduledChangeModal,
  }
})

interface LibraryComposable {
  canInviteMembers: ComputedRef<boolean>
  canInviteSchoolAdmins: ComputedRef<boolean>
  canInviteSchoolStudents: ComputedRef<boolean>
  canInviteSchoolTeachers: ComputedRef<boolean>
  canManageMembers: ComputedRef<boolean>
  canUpdateLibraryInfo: ComputedRef<boolean>
  currentLibrary: ComputedRef<Library>
  isClassroomLibrary: ComputedRef<boolean>
  isCurrentUserAtLeastLibraryTeacher: ComputedRef<boolean>
  isCurrentUserLibraryAdmin: ComputedRef<boolean>
  isCurrentUserLibraryOwner: ComputedRef<boolean>
  isSchoolLibrary: ComputedRef<boolean>
  isTeamLibrary: ComputedRef<boolean>
  libraries: ComputedRef<Library[]>
  libraryId: ComputedRef<LibraryId>
  libraryName: ComputedRef<string>
  librarySlug: ComputedRef<string>
  ownedLibraries: ComputedRef<Library[]>
  trialDaysLeft: ComputedRef<number>

  fetchViewableLibraries: () => void
}

const useLibrary = (): LibraryComposable => {
  // State

  // Getters
  const canInviteMembers = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.canInviteMembers)
  const canInviteSchoolAdmins = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.canInviteSchoolAdmins,
  )
  const canInviteSchoolStudents = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.canInviteSchoolStudents,
  )
  const canInviteSchoolTeachers = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.canInviteSchoolTeachers,
  )
  const canManageMembers = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.canManageMembers)
  const canUpdateLibraryInfo = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.canUpdateLibraryInfo,
  )
  const currentLibrary = computed((): Library => getVuexStore<DashboardSettingsState>()?.getters.currentLibrary)
  const isClassroomLibrary = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isClassroomLibrary)
  const isCurrentUserAtLeastLibraryTeacher = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isCurrentUserAtLeastLibraryTeacher,
  )
  const isCurrentUserLibraryAdmin = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isCurrentUserLibraryAdmin,
  )
  const isCurrentUserLibraryOwner = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isCurrentUserLibraryOwner,
  )
  const isSchoolLibrary = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isSchoolLibrary)
  const isTeamLibrary = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isTeamLibrary)
  const libraries = computed((): Library[] => getVuexStore<DashboardSettingsState>()?.getters.libraries ?? [])
  const libraryId = computed((): LibraryId => getVuexStore<DashboardSettingsState>()?.getters.libraryId)
  const libraryName = computed((): string => getVuexStore<DashboardSettingsState>()?.getters.libraryName)
  const librarySlug = computed((): string => getVuexStore<DashboardSettingsState>()?.getters.librarySlug)
  const ownedLibraries = computed((): Library[] => getVuexStore<DashboardSettingsState>()?.getters.ownedLibraries)
  const trialDaysLeft = computed((): number => getVuexStore<DashboardSettingsState>()?.getters.trialDaysLeft)

  // Actions
  function fetchViewableLibraries(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('fetchViewableLibraries', null, { root: true })
  }

  return {
    // State

    // Getters
    canInviteMembers,
    canInviteSchoolAdmins,
    canInviteSchoolStudents,
    canInviteSchoolTeachers,
    canManageMembers,
    canUpdateLibraryInfo,
    currentLibrary,
    isClassroomLibrary,
    isCurrentUserAtLeastLibraryTeacher,
    isCurrentUserLibraryAdmin,
    isCurrentUserLibraryOwner,
    isSchoolLibrary,
    isTeamLibrary,
    libraries,
    libraryId,
    libraryName,
    librarySlug,
    ownedLibraries,
    trialDaysLeft,

    // Actions
    fetchViewableLibraries,
  }
}

interface LibraryBillingComposable {
  estimatedTeamNeonDowngradeRefundAmount: ComputedRef<string | null>
  hasLibraryScheduledChanges: ComputedRef<boolean>
  isLibraryPaying: ComputedRef<boolean>
  isLoadingLibraryBillingInformation: ComputedRef<boolean>
  isLoadingTeamNeonDowngradeInformation: ComputedRef<boolean>
  isProcessingLibraryDowngrade: ComputedRef<boolean>
  libraryBilling: ComputedRef<any>
  libraryBillingError: ComputedRef<string>
  libraryDowngradeError: ComputedRef<string>
  libraryDowngradeStep: ComputedRef<LibraryDowngradeStep | null>
  xLibraryDowngradeModal: ComputedRef<boolean>

  changeStepLibraryDowngradeModal: ({ libraryDowngradeStep }: { libraryDowngradeStep: LibraryDowngradeStep }) => void
  closeLibraryDowngradeModal: () => void
  downgradeLibraryToTeamNeonImmediately: () => void
  scheduleLibraryDowngradeToTeamNeon: () => void
}

const useLibraryBilling = (): LibraryBillingComposable => {
  // State

  // Getters
  const estimatedTeamNeonDowngradeRefundAmount = computed(
    (): string | null => getVuexStore<DashboardSettingsState>()?.getters.estimatedTeamNeonDowngradeRefundAmount,
  )
  const hasLibraryScheduledChanges = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.hasLibraryScheduledChanges,
  )
  const isLibraryPaying = computed((): boolean => getVuexStore<DashboardSettingsState>()?.getters.isLibraryPaying)
  const isLoadingLibraryBillingInformation = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isLoadingLibraryBillingInformation,
  )
  const isLoadingTeamNeonDowngradeInformation = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isLoadingTeamNeonDowngradeInformation,
  )
  const isProcessingLibraryDowngrade = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isProcessingLibraryDowngrade,
  )
  const libraryBilling = computed((): any => getVuexStore<DashboardSettingsState>()?.getters.libraryBilling)
  const libraryBillingError = computed(
    (): string => getVuexStore<DashboardSettingsState>()?.getters.libraryBillingError,
  )
  const libraryDowngradeError = computed(
    (): string => getVuexStore<DashboardSettingsState>()?.getters.libraryDowngradeError,
  )
  const libraryDowngradeStep = computed(
    (): LibraryDowngradeStep | null => getVuexStore<DashboardSettingsState>()?.getters.libraryDowngradeStep,
  )
  const xLibraryDowngradeModal = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.xLibraryDowngradeModal,
  )

  // Actions
  const changeStepLibraryDowngradeModal = (payload: { libraryDowngradeStep: LibraryDowngradeStep }): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('changeStepLibraryDowngradeModal', payload, { root: true })
  }
  const closeLibraryDowngradeModal = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('closeLibraryDowngradeModal', null, { root: true })
  }
  const downgradeLibraryToTeamNeonImmediately = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('downgradeLibraryToTeamNeonImmediately', null, { root: true })
  }
  const scheduleLibraryDowngradeToTeamNeon = (): void => {
    void getVuexStore<DashboardSettingsState>()?.dispatch('scheduleLibraryDowngradeToTeamNeon', null, { root: true })
  }

  return {
    // State

    // Getters
    estimatedTeamNeonDowngradeRefundAmount,
    hasLibraryScheduledChanges,
    isLibraryPaying,
    isLoadingLibraryBillingInformation,
    isLoadingTeamNeonDowngradeInformation,
    isProcessingLibraryDowngrade,
    libraryBilling,
    libraryBillingError,
    libraryDowngradeError,
    libraryDowngradeStep,
    xLibraryDowngradeModal,

    // Actions
    changeStepLibraryDowngradeModal,
    closeLibraryDowngradeModal,
    downgradeLibraryToTeamNeonImmediately,
    scheduleLibraryDowngradeToTeamNeon,
  }
}

interface PersonalBillingComposable {
  billing: ComputedRef<Billing | null>
  isBillingInitialized: ComputedRef<boolean>
}

const usePersonalBilling = (): PersonalBillingComposable => {
  const billing = computed<Billing | null>(() => getVuexStore<DashboardSettingsState>()?.getters.billing)

  const isBillingInitialized = computed(
    (): boolean => getVuexStore<DashboardSettingsState>()?.getters.isBillingInitialized,
  )
  return {
    billing,
    isBillingInitialized,
  }
}

interface PersonalPlansComposable {
  currentScheduledChange: ComputedRef<ScheduledChange | null>
  oppositeSchedulePlan: ComputedRef<Plan | null>
  hasScheduledChanges: ComputedRef<boolean>
  isOfferedPlanCurrencyEqualToUserPlanCurrency: ComputedRef<boolean>
  isWebPlan: ComputedRef<boolean>
  showPlanCrossgradeModalCheckingCurrency: (payload: { userPlanCurrency: string }) => void
  showScheduledChangeModal: () => void
}

const usePersonalPlans = (billing: ComputedRef<Billing | null>): PersonalPlansComposable => {
  const personalPlansStore = usePersonalPlansStore()

  const currentScheduledChange = computed<ScheduledChange | null>(
    () => getVuexStore<DashboardSettingsState>()?.getters.currentScheduledChange,
  )

  const oppositeSchedulePlan = computed<Plan | null>(() => {
    if (billing.value?.tier == null) return null
    return personalPlansStore.plans[billing.value.tier][normalizedOppositePaymentSchedule(billing.value.period)]
  })

  const hasScheduledChanges = computed<boolean>(
    () => getVuexStore<DashboardSettingsState>()?.getters.hasScheduledChanges,
  )

  const currentOfferedPlan = computed<Plan | null>(() => {
    if (billing.value?.tier == null) return null
    return personalPlansStore.plans[billing.value.tier][normalizedPaymentSchedule(billing.value.period)]
  })

  const isWebPlan = computed<boolean>(() => billing.value?.payment_channel === 'chargebee')

  const isOfferedPlanCurrencyEqualToUserPlanCurrency = computed<boolean>(() => {
    return currentOfferedPlan.value?.currency === billing.value?.plan.currency
  })

  function showPlanCrossgradeModalCheckingCurrency(payload: { userPlanCurrency: string }): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showPlanCrossgradeModalCheckingCurrency', payload, {
      root: true,
    })
  }

  function showScheduledChangeModal(): void {
    void getVuexStore<DashboardSettingsState>()?.dispatch('showScheduledChangeModal')
  }

  return {
    // Getters
    currentScheduledChange,
    hasScheduledChanges,
    isOfferedPlanCurrencyEqualToUserPlanCurrency,
    isWebPlan,
    oppositeSchedulePlan,

    // Actions
    showPlanCrossgradeModalCheckingCurrency,
    showScheduledChangeModal,
  }
}
